import { Component, Output, EventEmitter, Input, HostListener, ElementRef, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StatusLead } from '../../../interfaces/status-lead.interface';
import { runtimeLogger } from '../../../utils/runtime-logger.util';
import { availableOriginsType } from './models/filter-field.model';
import { DashboardStoreService } from '../../../services/store/dashboard.store.service';

@Component({
  selector: 'app-filter-fields',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './filter-fields.html',
  styleUrls: ['./filter-fields.scss']
})
export class FilterFields implements OnChanges, OnInit {
  @Input() filterMode: 'default' | 'admin' = 'default';
  @Input() availableFilters: ('renovaMais' | 'preAprovado' | 'origem' | 'statusLead')[] = ['renovaMais', 'preAprovado', 'origem', 'statusLead'];
  @Input() availableOrigins: availableOriginsType[] = ['sempre-novo', 'ofertas-vw', 'campanha', 'S&D-PF', 'S&D-PJ', 'S&D-CONCESSIONARIOS', 'S&D-COBRANDED'];
  @Input() statusLeadFilters: StatusLead[] | undefined = undefined;
  @Input() selectedStatusLeadIdFilters: number[] = [];

  searchText: string = '';
  alphabeticalOrder: 'A-Z' | 'Z-A' | null = null;
  selectedFilters: string[] = [];
  startDate!: string;
  endDate!: string;
  showFilterPopup: boolean = false;
  adminSearchValue: string = '';

  selectedPreAprovacao: string | null = null;
  selectedRenova: string | null = null;
  selectedOrigem: string | null = null;

  @Input() adminFilterOptions: { value: string, label: string }[] = [];
  @Input() hasFilterTrigger: boolean = true;

  @Output() searchChange = new EventEmitter<string>();
  @Output() orderChange = new EventEmitter<'A-Z' | 'Z-A' | null>();
  @Output() filterChange = new EventEmitter<string[]>();
  @Output() filterStatusLeadChange = new EventEmitter<number[]>();
  @Output() dateRangeChange = new EventEmitter<{ start: string, end: string }>();
  @Output() clearAllFilters = new EventEmitter<void>();
  @Output() adminSearchChange = new EventEmitter<string>();

  private hasEmittedInitialDates = false;

  // Propriedades para modais mobile
  isFilterModalOpen: boolean = false;
  isDatePickerModalOpen: boolean = false;

  // Valores temporários para o modal de filtro
  tempSelectedFilters: string[] = [];
  tempSelectedStatusLeadIdFilters: number[] = [];
  tempSelectedPreAprovacao: string | null = null;
  tempSelectedRenova: string | null = null;
  tempSelectedOrigem: string | null = null;

  // Valores temporários para o modal de data
  tempStartDate: string = '';
  tempEndDate: string = '';

  constructor(
    private eRef: ElementRef,
    private dashboardStoreService: DashboardStoreService
  ) {

  }

  ngOnInit(): void {
    this.startDate = this.getThreeMonthsAgoDate();
    this.endDate = this.getTodayDate();

    this.syncFilterStates();
    this.emitInitialValues();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedStatusLeadIdFilters']) {
      this.selectedStatusLeadIdFilters = [...(this.selectedStatusLeadIdFilters ?? [])];
    }

    if (changes['statusLeadFilters'] || changes['selectedStatusLeadIdFilters']) {
      this.syncFilterStates();
    }

    if (changes['filterMode'] && !changes['filterMode'].firstChange) {
      this.resetFieldsForMode();
    }
  }

  private emitInitialValues(): void {
    if (!this.hasEmittedInitialDates && this.startDate && this.endDate) {
      this.dashboardStoreService.setStartDate(this.startDate);
      this.dashboardStoreService.setEndDate(this.endDate);
      this.dateRangeChange.emit({
        start: this.startDate,
        end: this.endDate
      });
      this.hasEmittedInitialDates = true;
    }
  }

  private getTodayDate(): string {
    const endDateService = this.dashboardStoreService.endDate();

    if (endDateService) {
      return endDateService;
    }

    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  private getThreeMonthsAgoDate(): string {
    const startDateService = this.dashboardStoreService?.startDate();

    if (startDateService) {
      return startDateService
    }

    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    return threeMonthsAgo.toISOString().split('T')[0];
  }

  private resetFieldsForMode(): void {
    this.searchText = '';
    this.adminSearchValue = '';
    this.selectedFilters = [];
    this.selectedStatusLeadIdFilters = [];
    this.selectedPreAprovacao = null;
    this.selectedRenova = null;
    this.selectedOrigem = null;

    this.startDate = this.getThreeMonthsAgoDate();
    this.endDate = this.getTodayDate();
    this.hasEmittedInitialDates = false;

    this.searchChange.emit('');
    this.adminSearchChange.emit('');
    this.filterChange.emit([]);
    this.filterStatusLeadChange.emit([]);

    this.emitInitialValues();
  }

  private syncFilterStates(): void {
    if (this.filterMode === 'default') {
      const preAprovacaos = ['Pré Aprovação: Pré Aprovado', 'Pré Aprovação: Necessita Análise', 'Pré Aprovação: Não Solicitado'];
      const selectedPreAprovacao = preAprovacaos.find(op => this.selectedFilters.includes(op));
      this.selectedPreAprovacao = selectedPreAprovacao || null;

      if (this.selectedFilters.includes('Renova+: Sim')) {
        this.selectedRenova = 'Sim';
      } else if (this.selectedFilters.includes('Renova+: Não')) {
        this.selectedRenova = 'Não';
      } else {
        this.selectedRenova = null;
      }

      const origens = ['Origem: Sempre Novo', 'Origem: Ofertas VW', 'Origem: Campanha', 'Origem: S&D PF', 'Origem: S&D PJ', 'Origem: S&D CONCESSIONARIOS', 'Origem: S&D COBRANDED'];
      const selectedOrigem = origens.find(op => this.selectedFilters.includes(op));
      this.selectedOrigem = selectedOrigem || null;
    }
  }

  getDisplayDate(dateString: string): string {
    if (!dateString) return '';

    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  }

  toggleAlphabeticalOrder() {
    if (this.filterMode === 'default') {
      if (this.alphabeticalOrder === null) {
        this.alphabeticalOrder = 'A-Z';
      } else if (this.alphabeticalOrder === 'A-Z') {
        this.alphabeticalOrder = 'Z-A';
      } else {
        this.alphabeticalOrder = null;
      }
      this.orderChange.emit(this.alphabeticalOrder);
    }
  }

  removeAlphabeticalOrder() {
    if (this.filterMode === 'default') {
      this.alphabeticalOrder = null;
      this.orderChange.emit(null);
    }
  }

  toggleFilterPopup() {
    if (this.filterMode === 'default') {
      if (window.innerWidth <= 768) {
        this.openFilterModal();
      } else {
        this.showFilterPopup = !this.showFilterPopup;
      }
    }
  }

  toggleFilter(filter: string) {
    if (this.filterMode === 'default') {
      if (this.isFilterSelected(filter)) {
        this.selectedFilters = this.selectedFilters.filter(f => f !== filter);
      } else {
        this.selectedFilters.push(filter);
      }
      this.filterChange.emit(this.selectedFilters);
    }
  }

  isAvailableFilter(filter: 'renovaMais' | 'preAprovado' | 'origem' | 'statusLead'): boolean {
    return this.filterMode === 'default' && this.availableFilters.includes(filter);
  }

  isFilterSelected(filter: string): boolean {
    return this.selectedFilters.includes(filter);
  }

  togglePreAprovacao(value: string) {
    if (this.filterMode === 'default') {
      if (this.selectedPreAprovacao === value) {
        this.selectedPreAprovacao = null;
        this.selectedFilters = this.selectedFilters.filter(f => f !== value);
      } else {
        this.selectedPreAprovacao = value;
        this.selectedFilters = this.selectedFilters.filter(f =>
          f !== 'Pré Aprovação: Pré Aprovado' &&
          f !== 'Pré Aprovação: Necessita Análise' &&
          f !== 'Pré Aprovação: Não Solicitado'
        );
        this.selectedFilters.push(value);
      }
      this.filterChange.emit(this.selectedFilters);
    }
  }

  isPreAprovacaoSelected(value: string): boolean {
    return this.selectedPreAprovacao === value || this.selectedFilters.includes(value);
  }

  toggleRenova(value: string) {
    if (this.filterMode === 'default') {
      if (this.selectedRenova === value) {
        this.selectedRenova = null;
        this.selectedFilters = this.selectedFilters.filter(f => f !== value);
      } else {
        this.selectedRenova = value;
        this.selectedFilters = this.selectedFilters.filter(f => f !== 'Renova+: Sim' && f !== 'Renova+: Não');
        this.selectedFilters.push(value);
      }
      this.filterChange.emit(this.selectedFilters);
    }
  }

  isRenovaSelected(value: string): boolean {
    return this.selectedRenova === value || this.selectedFilters.includes(value);
  }

  toggleOrigem(value: string) {
    if (this.filterMode === 'default') {
      if (this.selectedOrigem === value) {
        this.selectedOrigem = null;
        this.selectedFilters = this.selectedFilters.filter(f => f !== value);
      } else {
        this.selectedOrigem = value;
        this.selectedFilters = this.selectedFilters.filter(f =>
          f !== 'Origem: Sempre Novo' &&
          f !== 'Origem: Ofertas VW' &&
          f !== 'Origem: Campanha' &&
          f !== 'Origem: S&D PF' &&
          f !== 'Origem: S&D PJ' &&
          f !== 'Origem: S&D CONCESSIONARIOS' &&
          f !== 'Origem: S&D COBRANDED'
        );
        this.selectedFilters.push(value);
      }
      this.filterChange.emit(this.selectedFilters);
    }
  }

  isOrigemSelected(value: string): boolean {
    return this.selectedOrigem === value || this.selectedFilters.includes(value);
  }

  removeFilter(filter: string) {
    if (this.filterMode === 'default') {
      this.selectedFilters = this.selectedFilters.filter(f => f !== filter);

      if (filter.startsWith('Pré Aprovação:')) {
        this.selectedPreAprovacao = null;
      }
      if (filter.startsWith('Renova+:')) {
        this.selectedRenova = null;
      }
      if (filter.startsWith('Origem:')) {
        this.selectedOrigem = null;
      }

      this.filterChange.emit(this.selectedFilters);
    }
  }

  toggleStatusLeadIdFilter(statusLeadId: number) {
    if (this.filterMode === 'default') {
      if (this.isStatusLeadIdFilterSelected(statusLeadId)) {
        this.selectedStatusLeadIdFilters = this.selectedStatusLeadIdFilters.filter(f => f !== statusLeadId);
      } else {
        this.selectedStatusLeadIdFilters.push(statusLeadId);
      }
      this.filterStatusLeadChange.emit(this.selectedStatusLeadIdFilters);
    }
  }

  getSelectedStatusLeadNomeFilterById(statusLeadId: number) {
    return this.statusLeadFilters?.find(item => item.id === statusLeadId)?.nome;
  }

  isStatusLeadIdFilterSelected(statusLeadId: number): boolean {
    return this.selectedStatusLeadIdFilters.includes(statusLeadId);
  }

  removeStatusLeadIdFilter(statusLeadId: number) {
    if (this.filterMode === 'default') {
      this.selectedStatusLeadIdFilters = this.selectedStatusLeadIdFilters.filter(f => f !== statusLeadId);
      this.filterStatusLeadChange.emit(this.selectedStatusLeadIdFilters);
    }
  }

  onSearchChange() {
    if (this.filterMode === 'default') {
      this.searchChange.emit(this.searchText);
    }
  }

  onAdminSearchChange() {
    if (this.filterMode === 'admin') {
      this.adminSearchChange.emit(this.adminSearchValue);
    }
  }

  onDateChange() {
    runtimeLogger.debug('FilterFields - onDateChange:', {
      start: this.startDate,
      end: this.endDate
    });

    this.dashboardStoreService.setStartDate(this.startDate);
    this.dashboardStoreService.setEndDate(this.endDate);
    this.dateRangeChange.emit({
      start: this.startDate,
      end: this.endDate
    });
  }

  // ================== MÉTODOS PARA MODAL DE DATA MOBILE ==================
  openDatePickerModal() {
    this.tempStartDate = this.startDate;
    this.tempEndDate = this.endDate;
    this.isDatePickerModalOpen = true;
  }

  closeDatePickerModal() {
    this.isDatePickerModalOpen = false;
  }

  confirmDateSelection() {
    this.startDate = this.tempStartDate;
    this.endDate = this.tempEndDate;
    this.onDateChange();
    this.closeDatePickerModal();
  }

  // ================== MÉTODOS PARA MODAL DE FILTRO MOBILE ==================
  openFilterModal() {
    if (this.filterMode === 'default') {
      // Copiar valores atuais para os temporários
      this.tempSelectedFilters = [...this.selectedFilters];
      this.tempSelectedStatusLeadIdFilters = [...this.selectedStatusLeadIdFilters];
      this.tempSelectedPreAprovacao = this.selectedPreAprovacao;
      this.tempSelectedRenova = this.selectedRenova;
      this.tempSelectedOrigem = this.selectedOrigem;
      this.isFilterModalOpen = true;
    }
  }

  closeFilterModal() {
    this.isFilterModalOpen = false;
  }

  confirmFilterSelection() {
    if (this.filterMode === 'default') {
      // Aplicar as seleções temporárias
      this.selectedFilters = [...this.tempSelectedFilters];
      this.selectedStatusLeadIdFilters = [...this.tempSelectedStatusLeadIdFilters];
      this.selectedPreAprovacao = this.tempSelectedPreAprovacao;
      this.selectedRenova = this.tempSelectedRenova;
      this.selectedOrigem = this.tempSelectedOrigem;

      // Emitir os eventos
      this.filterChange.emit(this.selectedFilters);
      this.filterStatusLeadChange.emit(this.selectedStatusLeadIdFilters);
    }
    this.closeFilterModal();
  }

  // Métodos temporários para o modal de filtro
  isTempFilterSelected(filter: string): boolean {
    return this.tempSelectedFilters.includes(filter);
  }

  toggleTempFilter(filter: string) {
    if (this.isTempFilterSelected(filter)) {
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f => f !== filter);
    } else {
      this.tempSelectedFilters.push(filter);
    }
  }

  isTempPreAprovacaoSelected(value: string): boolean {
    return this.tempSelectedPreAprovacao === value || this.tempSelectedFilters.includes(value);
  }

  toggleTempPreAprovacao(value: string) {
    if (this.tempSelectedPreAprovacao === value) {
      this.tempSelectedPreAprovacao = null;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f => f !== value);
    } else {
      this.tempSelectedPreAprovacao = value;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f =>
        f !== 'Pré Aprovação: Pré Aprovado' &&
        f !== 'Pré Aprovação: Necessita Análise' &&
        f !== 'Pré Aprovação: Não Solicitado'
      );
      this.tempSelectedFilters.push(value);
    }
  }

  isTempRenovaSelected(value: string): boolean {
    return this.tempSelectedRenova === value || this.tempSelectedFilters.includes(value);
  }

  toggleTempRenova(value: string) {
    if (this.tempSelectedRenova === value) {
      this.tempSelectedRenova = null;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f => f !== value);
    } else {
      this.tempSelectedRenova = value;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f => f !== 'Renova+: Sim' && f !== 'Renova+: Não');
      this.tempSelectedFilters.push(value);
    }
  }

  isTempOrigemSelected(value: string): boolean {
    return this.tempSelectedOrigem === value || this.tempSelectedFilters.includes(value);
  }

  toggleTempOrigem(value: string) {
    if (this.tempSelectedOrigem === value) {
      this.tempSelectedOrigem = null;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f => f !== value);
    } else {
      this.tempSelectedOrigem = value;
      this.tempSelectedFilters = this.tempSelectedFilters.filter(f =>
        f !== 'Origem: Sempre Novo' &&
        f !== 'Origem: Ofertas VW' &&
        f !== 'Origem: Campanha' &&
        f !== 'Origem: S&D PF' &&
        f !== 'Origem: S&D PJ' &&
        f !== 'Origem: S&D CONCESSIONARIOS' &&
        f !== 'Origem: S&D COBRANDED'
      );
      this.tempSelectedFilters.push(value);
    }
  }

  isTempStatusLeadIdFilterSelected(statusLeadId: number): boolean {
    return this.tempSelectedStatusLeadIdFilters.includes(statusLeadId);
  }

  toggleTempStatusLeadIdFilter(statusLeadId: number) {
    if (this.isTempStatusLeadIdFilterSelected(statusLeadId)) {
      this.tempSelectedStatusLeadIdFilters = this.tempSelectedStatusLeadIdFilters.filter(f => f !== statusLeadId);
    } else {
      this.tempSelectedStatusLeadIdFilters.push(statusLeadId);
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (this.showFilterPopup && !this.eRef.nativeElement.contains(event.target)) {
      this.showFilterPopup = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (this.showFilterPopup && keyboardEvent.key === 'Escape') {
      this.showFilterPopup = false;
    }
  }

  clearAll() {
    let isAnyFilterApplied = false;

    if (this.filterMode === 'default') {
      isAnyFilterApplied =
        (this.searchText && this.searchText.trim().length > 0) ||
        !!this.alphabeticalOrder ||
        (this.selectedFilters && this.selectedFilters.length > 0) ||
        (this.selectedStatusLeadIdFilters && this.selectedStatusLeadIdFilters.length > 0) ||
        (!!this.startDate && this.startDate !== '') ||
        (!!this.endDate && this.endDate !== '') ||
        !!this.selectedPreAprovacao ||
        !!this.selectedRenova ||
        !!this.selectedOrigem;
    } else {
      isAnyFilterApplied =
        (this.adminSearchValue && this.adminSearchValue.trim().length > 0) ||
        (!!this.startDate && this.startDate !== '') ||
        (!!this.endDate && this.endDate !== '');
    }

    if (!isAnyFilterApplied) {
      return;
    }

    if (this.filterMode === 'default') {
      this.searchText = '';
      this.alphabeticalOrder = null;
      this.selectedFilters = [];
      this.selectedStatusLeadIdFilters = [];
      this.selectedPreAprovacao = null;
      this.selectedRenova = null;
      this.selectedOrigem = null;

      this.searchChange.emit('');
      this.orderChange.emit(null);
      this.filterChange.emit([]);
      this.filterStatusLeadChange.emit([]);
    } else {
      this.adminSearchValue = '';
      this.adminSearchChange.emit('');
    }

    // Resetar datas para vazias
    this.startDate = '';
    this.endDate = '';
    this.showFilterPopup = false;

    // NÃO emitir dateRangeChange aqui - o onClearAllFilters vai lidar com tudo
    this.clearAllFilters.emit();
  }

  get today(): string {
    return this.getTodayDate();
  }

  isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  // Adicione este método na classe FilterFields
  getTotalFilterCount(): number {
    if (this.filterMode === 'default') {
      return this.selectedFilters.length + this.selectedStatusLeadIdFilters.length;
    }
    return 0;
  }
}
